export const MIN_STREAM_ASPECT_DIMENSION = 1;
export const MAX_STREAM_ASPECT_DIMENSION = 8192;
export const MAX_STREAM_ASPECT_RATIO = 4;

export type StreamVideoSize = {
  width: number;
  height: number;
};

export type StreamVideoOrientation = 'portrait' | 'landscape' | 'square';

export function gcd(a: number, b: number) {
  let x = Math.abs(Math.round(a));
  let y = Math.abs(Math.round(b));
  while (y) {
    const next = x % y;
    x = y;
    y = next;
  }
  return x || 1;
}

export function normalizeStreamSize(width: number, height: number): StreamVideoSize | null {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (width <= 0 || height <= 0) return null;
  let nextWidth = Math.min(MAX_STREAM_ASPECT_DIMENSION, Math.max(MIN_STREAM_ASPECT_DIMENSION, Math.round(width)));
  let nextHeight = Math.min(
    MAX_STREAM_ASPECT_DIMENSION,
    Math.max(MIN_STREAM_ASPECT_DIMENSION, Math.round(height))
  );
  const aspect = nextWidth / nextHeight;
  if (aspect > MAX_STREAM_ASPECT_RATIO) {
    nextWidth = Math.round(nextHeight * MAX_STREAM_ASPECT_RATIO);
  } else if (aspect < 1 / MAX_STREAM_ASPECT_RATIO) {
    nextHeight = Math.round(nextWidth * MAX_STREAM_ASPECT_RATIO);
  }
  if (nextWidth < MIN_STREAM_ASPECT_DIMENSION || nextHeight < MIN_STREAM_ASPECT_DIMENSION) {
    return null;
  }
  return { width: nextWidth, height: nextHeight };
}

export function reduceStreamRatio(width: number, height: number): StreamVideoSize | null {
  const size = normalizeStreamSize(width, height);
  if (!size) return null;
  const divisor = gcd(size.width, size.height);
  return {
    width: size.width / divisor,
    height: size.height / divisor,
  };
}

export function orientationFromSize(width: number, height: number): StreamVideoOrientation | null {
  const size = normalizeStreamSize(width, height);
  if (!size) return null;
  if (size.height === size.width) return 'square';
  return size.height > size.width ? 'portrait' : 'landscape';
}

export function cssAspectFromSize(width: number, height: number) {
  const ratio = reduceStreamRatio(width, height);
  return ratio ? `${ratio.width} / ${ratio.height}` : '';
}

export function applyStreamAspectToElement(el: HTMLElement, width: number, height: number) {
  const ratio = reduceStreamRatio(width, height);
  const orientation = orientationFromSize(width, height);
  if (!ratio || !orientation) return;
  el.dataset.orientation = orientation;
  el.style.setProperty('--mw-stream-aspect', `${ratio.width} / ${ratio.height}`);
  el.style.setProperty('--mw-stream-w', String(ratio.width));
  el.style.setProperty('--mw-stream-h', String(ratio.height));
}
